import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Ticket, TimerReset, CheckCircle2, Package, BookOpen, Users, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value, hint, testid, delay = 0 }) {
  return (
    <Card
      className="animate-in-up border-border transition-transform hover:-translate-y-0.5 hover:shadow-sm"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testid}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            <div className="mt-3 font-heading text-3xl font-semibold tracking-tight">{value ?? "—"}</div>
            {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
          </div>
          <div className="rounded-md border border-border p-2 text-muted-foreground">
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Visão geral</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl">Dashboard</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Acompanhe o desempenho do suporte, inventário e base de conhecimento em um só lugar.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/tickets"><Button variant="outline" data-testid="btn-goto-tickets">Ver Chamados</Button></Link>
          {user.role !== "cliente" && (
            <Link to="/inventory"><Button data-testid="btn-goto-inventory">Gerenciar Inventário</Button></Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Ticket} label="Total de chamados" value={stats.total_tickets} testid="stat-total-tickets" delay={0} />
        <Stat icon={AlertTriangle} label="Abertos" value={stats.open_tickets} testid="stat-open-tickets" delay={60} />
        <Stat icon={TimerReset} label="Em andamento" value={stats.in_progress} testid="stat-in-progress" delay={120} />
        <Stat icon={CheckCircle2} label="Resolvidos" value={stats.resolved} testid="stat-resolved" delay={180} />
      </div>

      {user.role !== "cliente" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Package} label="Itens no inventário" value={stats.inventory_items} testid="stat-inventory" delay={0} />
          <Stat icon={AlertTriangle} label="Estoque baixo" value={stats.low_stock} testid="stat-low-stock"
                hint="Itens abaixo do mínimo" delay={80} />
          <Stat icon={BookOpen} label="Artigos KB" value={stats.kb_articles} testid="stat-kb" delay={160} />
          {user.role === "admin" && (
            <Stat icon={Users} label="Usuários" value={stats.total_users} testid="stat-users" delay={240} />
          )}
        </div>
      )}

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Bem-vindo, {user.name.split(" ")[0]}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {user.role === "admin" && "Você tem acesso completo. Gerencie usuários, inventário, chamados, base de conhecimento e a integração com WhatsApp."}
          {user.role === "tecnico" && "Você pode atender e criar chamados, gerenciar inventário, escrever artigos e usar o chat + WhatsApp."}
          {user.role === "cliente" && "Abra chamados para reportar problemas e acompanhe o andamento. Consulte a base de conhecimento antes de abrir."}
        </CardContent>
      </Card>
    </div>
  );
}
